import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Flex, Table } from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import { ExportOutlined } from '@ant-design/icons';
import fileDownload from 'js-file-download';
import Metric from '../../stats/Metric';
import MetricsContainer from '../../stats/MetricsContainer';
import HelperMethods from '../../../services/exports/HelperMethods';
import useApiClient from '../../../hooks/api/useApiClient';
import { useGetSalesTeamStatsQuery } from '../../../store/api-slices';
import ManagerSelect from '../../form/business/ManagerSelect';
import usePermissions, { ManagerPermission, ManagerRole } from '../../../hooks/auth/usePermissions';
import { TeamReportFormat, TeamReportType } from '../../../services/exports/Constants';
import useHelpers from '../../../hooks/context/useHelpers';

function CollectedCommissionReport() {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Team:Reports:CollectedCommissionReport' });

  const { TeamReportsManager } = useApiClient();
  const { can } = usePermissions();

  const { profile } = useSelector((state) => state.initialData);
  const [month, setMonth] = useState(() => dayjs());
  const [selectedManagers, setSelectedManagers] = useState(
    can(ManagerPermission.GlobalReadSalesTeamStats) ? [] : [profile?.id],
  );

  const [report, setReport] = useState(null);
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [exporting, setExporting] = useState(false);
  const getMonthDetails = useCallback((_month) => details[_month.valueOf()] ?? {}, [details]);

  const onLoadManagers = ({ data, setManagers }) => {
    setManagers(data);
    setSelectedManagers(collect(data).pluck('id').toArray());
  };

  useEffect(() => {
    if (selectedManagers.length === 0) {
      return;
    }

    if (can(ManagerPermission.GlobalReadSalesTeamStats)) {
      loadReport('json');
    } else {
      loadDetails(selectedManagers[0]);
    }
  }, [selectedManagers, month.valueOf()]);

  const loadReport = async (format) => {
    format === TeamReportFormat.JSON && setDetails({});
    format === TeamReportFormat.JSON ? setLoading(true) : setExporting(true);
    const { success, data } = await TeamReportsManager.create({
      format,
      type: TeamReportType.SalesCommission,
      start: month.startOf('month').toDate(),
      end: month.endOf('month').toDate(),
      managers: selectedManagers,
    });
    format === TeamReportFormat.JSON ? setLoading(false) : setExporting(false);

    if (!success) {
      toast.error(t('toasts.failed_to_load_data'));

      return null;
    }

    format === TeamReportFormat.JSON && setReport(data);

    return data;
  };

  const loadDetails = async (managerId) => {
    if (getMonthDetails(month.valueOf())[managerId] ?? null) {
      return;
    }

    setLoadingDetails(managerId);
    const { success, data } = await TeamReportsManager.loadManagerDetails({
      format: TeamReportFormat.JSON,
      type: TeamReportType.SalesCommission,
      start: month.startOf('month').toDate(),
      end: month.endOf('month').toDate(),
      managers: [managerId],
    });
    setLoadingDetails(null);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    return setDetails((current) => ({
      ...current,
      [month.valueOf()]: {
        ...(current[month.valueOf()] ?? {}),
        [managerId]: data,
      },
    }));
  };

  return (
    <>
      <div className="space-y-4">
        <Flex justify="space-between" gap="large">
          <Flex gap="middle" className="items-center">
            <DatePicker
              value={month}
              onChange={setMonth}
              allowClear={false}
              picker="month"
              format="MMMM YYYY"
              className="min-w-[160px] h-fit my-auto"
            />
            {can(ManagerPermission.GlobalReadSalesTeamStats) && (
              <ManagerSelect
                value={selectedManagers}
                isMulti
                roles={[ManagerRole.SalesManager, ManagerRole.SalesLeadership]}
                onChange={(value) => setSelectedManagers(value)}
                onLoad={onLoadManagers}
                className="my-auto"
                placeholder={t('filters.managers.placeholder')}
              />
            )}
          </Flex>
          {can(ManagerPermission.GlobalReadSalesTeamStats) && (
            <Button
              type="primary"
              size="large"
              loading={exporting}
              icon={<ExportOutlined />}
              onClick={() => {
                const filename = `sales-commission-report-${month.format('MMMM')}.xls`;
                loadReport(TeamReportFormat.File).then((data) => data && fileDownload(data, filename));
              }}
            >
              {t('buttons.export')}
            </Button>
          )}
        </Flex>
      </div>
      <div className="mt-xl">
        <Metrics month={month} selectedManagers={selectedManagers} />
      </div>
      <div className="mt-xl">
        {can(ManagerPermission.GlobalReadSalesTeamStats) ? (
          <ManagersTable
            data={report}
            details={getMonthDetails(month.valueOf())}
            loading={loading}
            loadingDetails={loadingDetails}
            loadDetails={loadDetails}
          />
        ) : (
          <CompaniesTable
            managerId={profile?.id}
            data={getMonthDetails(month.valueOf())[profile?.id] ?? []}
            loadingDetails={loadingDetails}
          />
        )}
      </div>
    </>
  );
}

function Metrics({ month, selectedManagers }) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Team:Reports:CollectedCommissionReport.metrics' });

  const { StatsManager } = useApiClient();

  const { formatCurrency } = useHelpers();

  const formattedMonth = month.format('YYYY-MM-DD');
  const { data, isFetching } = useGetSalesTeamStatsQuery({
    apiManager: StatsManager,
    month: formattedMonth,
    managers: selectedManagers,
  });

  const getItem = (item) => {
    if (!item) {
      return;
    }

    return {
      value: item.current ?? 0,
      previous: item.prev,
      change: item.prev ? HelperMethods.percentagePart(item.current - item.prev, item.prev) : null,
    };
  };

  return (
    <MetricsContainer
      metrics={[
        <Metric
          loading={isFetching}
          key="collected_commission"
          name={t('collected_commission')}
          items={getItem(data?.collected_commission)}
          formatter={formatCurrency}
          excludedHistoryMetrics={['*']}
        />,
      ]}
    />
  );
}

function ManagersTable({ data, details, loading, loadingDetails, loadDetails }) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Team:Reports:CollectedCommissionReport.fields' });

  const { formatCurrency } = useHelpers();

  return (
    <Table
      dataSource={data}
      columns={[
        {
          title: t('manager'),
          key: 'name',
          dataIndex: 'manager_name',
        },
        {
          title: t('area'),
          key: 'location',
          dataIndex: 'manager_location',
        },
        {
          title: t('total_amount'),
          key: 'total_amount',
          render: (item) => formatCurrency(item.total_amount),
        },
        {
          title: t('companies_live'),
          key: 'companies_live',
          render: (item) => (
            <div className="rounded-lg p-xs bg-brand-primary/25 border-brand-primary border-solid size-8 flex justify-center items-center">
              {item.companies_live ?? 0}
            </div>
          ),
        },
      ]}
      pagination={false}
      loading={loading}
      expandable={{
        onExpand: (expanded, record) => expanded && loadDetails(record.id),
        expandedRowRender: (item) => (
          <CompaniesTable managerId={item.id} data={details[item.id] ?? []} loadingDetails={loadingDetails} />
        ),
      }}
      className="rounded-none shadow-card bg-white overflow-hidden"
      rowKey={(item) => item.id}
    />
  );
}

function CompaniesTable({ managerId, data, loadingDetails }) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Team:Reports:CollectedCommissionReport.fields' });

  const { formatDateTime, formatCurrency } = useHelpers();

  return (
    <Table
      columns={[
        { title: t('restaurant'), key: 'name', dataIndex: 'name' },
        {
          title: t('live_from'),
          key: 'live_from',
          render: (item) => formatDateTime(item.live_from, { timeFormat: '' }),
        },
        ...[
          'total_amount',
          'plan_amount',
          'prepayment_bonus_amount',
          'lead_source_bonus_amount',
          'google_business_bonus_amount',
          'promo_code_bonus_amount',
          'smart_pricing_bonus_amount',
          'mobile_app_bonus_amount',
          'sales_goal_bonus_amount',
        ].map((field) => ({
          title: t(field),
          key: field,
          render: (item) => formatCurrency(item[field]),
        })),
      ]}
      loading={loadingDetails === managerId}
      dataSource={data}
      rowKey={(row) => `company-${row.id}`}
      pagination={false}
    />
  );
}

export default CollectedCommissionReport;
